import { useRef, useState } from 'react';
import { FiMapPin, FiCrosshair, FiLoader } from 'react-icons/fi';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Button } from './ui/Button';
import { MapaSeletor, type LocalSelecionado } from './MapaSeletor';
import { buscarCep, geocodificarEndereco, reverseGeocode } from '../lib/geo';

export interface EnderecoMapaValor {
  logradouro: string;
  numero: string;
  bairro: string;
  cep: string;
  cidade: string;
  uf: string;
  latitude: number | null;
  longitude: number | null;
}

export const ENDERECO_VAZIO: EnderecoMapaValor = {
  logradouro: '', numero: '', bairro: '', cep: '', cidade: '', uf: '', latitude: null, longitude: null,
};

/** Monta a string de endereço ("Rua, nº") a partir do valor. */
export function enderecoComoTexto(v: EnderecoMapaValor): string {
  return [v.logradouro, v.numero].map((s) => s.trim()).filter(Boolean).join(', ');
}

interface Props {
  valor: EnderecoMapaValor;
  onChange: (v: EnderecoMapaValor) => void;
  /** Mostra o botão "Usar minha localização" (GPS). */
  mostrarGps?: boolean;
  /** Mostra o botão/painel de mapa. */
  mostrarMapa?: boolean;
}

/**
 * Endereço + mapa com geocodificação nos dois sentidos (item 2.70):
 * - CEP digitado -> ViaCEP preenche o endereço -> geocodifica e marca o pin.
 * - Número digitado -> geocodifica o endereço e marca o pin.
 * - Clique no mapa -> reverse-geocode preenche rua/número/bairro/CEP.
 * - GPS -> marca o pin e preenche o endereço.
 */
export function EnderecoComMapa({ valor, onChange, mostrarGps = true, mostrarMapa = true }: Props) {
  const [mapaAberto, setMapaAberto] = useState(false);
  const [obtendoGps, setObtendoGps] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const valorRef = useRef(valor);
  valorRef.current = valor;

  function patch(p: Partial<EnderecoMapaValor>) {
    onChange({ ...valorRef.current, ...p });
  }

  // CEP -> ViaCEP -> preenche endereço e tenta geocodificar.
  async function aoSairCep() {
    const limpo = valorRef.current.cep.replace(/\D/g, '');
    if (limpo.length !== 8) return;
    setBuscando(true);
    setAviso(null);
    const e = await buscarCep(limpo);
    if (!e) {
      setBuscando(false);
      setAviso('CEP não encontrado. Você pode informar o endereço manualmente.');
      return;
    }
    const base = valorRef.current;
    const novo: EnderecoMapaValor = {
      ...base,
      logradouro: e.logradouro || base.logradouro,
      bairro: e.bairro || base.bairro,
      cidade: e.cidade || base.cidade,
      uf: e.uf || base.uf,
      cep: e.cep,
    };
    onChange(novo);
    const geo = await geocodificarEndereco({
      logradouro: novo.logradouro, numero: novo.numero, cidade: novo.cidade, uf: novo.uf, cep: novo.cep,
    });
    setBuscando(false);
    if (geo) {
      onChange({ ...novo, latitude: geo.lat, longitude: geo.lng });
      if (mostrarMapa) setMapaAberto(true);
    }
  }

  // Número (ou endereço) -> geocodifica e marca o pin.
  async function geocodificarAtual() {
    const v = valorRef.current;
    if (!v.logradouro && v.cep.replace(/\D/g, '').length !== 8) return;
    setBuscando(true);
    const geo = await geocodificarEndereco({
      logradouro: v.logradouro, numero: v.numero, cidade: v.cidade, uf: v.uf, cep: v.cep,
    });
    setBuscando(false);
    if (geo) {
      patch({ latitude: geo.lat, longitude: geo.lng });
      if (mostrarMapa) setMapaAberto(true);
    }
  }

  // Clique no mapa -> preenche o endereço completo.
  function aoClicarMapa(local: LocalSelecionado) {
    const base = valorRef.current;
    onChange({
      ...base,
      latitude: local.lat,
      longitude: local.lng,
      logradouro: local.logradouro || base.logradouro,
      numero: local.numero || base.numero,
      bairro: local.bairro || base.bairro,
      cidade: local.cidade || base.cidade,
      uf: local.uf || base.uf,
      cep: local.cep || base.cep,
    });
  }

  function usarGps() {
    if (!navigator.geolocation) { setAviso('Seu navegador não suporta geolocalização.'); return; }
    setObtendoGps(true);
    setAviso(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const rev = await reverseGeocode(lat, lng);
        setObtendoGps(false);
        const base = valorRef.current;
        onChange({
          ...base,
          latitude: lat,
          longitude: lng,
          ...(rev ? {
            logradouro: rev.logradouro || base.logradouro,
            numero: rev.numero || base.numero,
            bairro: rev.bairro || base.bairro,
            cidade: rev.cidade || base.cidade,
            uf: rev.uf || base.uf,
            cep: rev.cep || base.cep,
          } : {}),
        });
        if (mostrarMapa) setMapaAberto(true);
      },
      () => { setObtendoGps(false); setAviso('Não foi possível obter a localização. Informe o endereço manualmente.'); },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }

  const temPino = valor.latitude !== null && valor.longitude !== null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {mostrarGps && (
          <Button type="button" variant="outline" loading={obtendoGps} onClick={usarGps}>
            <FiCrosshair size={14} /> Usar minha localização
          </Button>
        )}
        {mostrarMapa && (
          <Button type="button" variant="outline" onClick={() => setMapaAberto((v) => !v)}>
            <FiMapPin size={14} /> {mapaAberto ? 'Ocultar mapa' : 'Escolher no mapa'}
          </Button>
        )}
        {buscando && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <FiLoader size={12} className="animate-spin" /> Buscando localização…
          </span>
        )}
        {temPino && !buscando && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">Local marcado ✓</span>
        )}
      </div>

      {aviso && <p className="text-xs text-amber-700">{aviso}</p>}

      {mostrarMapa && mapaAberto && (
        <MapaSeletor
          posicao={temPino ? [valor.latitude as number, valor.longitude as number] : null}
          posicaoInicial={temPino ? [valor.latitude as number, valor.longitude as number] : null}
          onSelecionar={aoClicarMapa}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-6 gap-x-4 gap-y-3">
        <div className="md:col-span-2">
          <Label htmlFor="end_cep">CEP</Label>
          <Input
            id="end_cep"
            value={valor.cep}
            onChange={(e) => patch({ cep: e.target.value })}
            onBlur={aoSairCep}
            placeholder="00000-000"
            inputMode="numeric"
            maxLength={9}
          />
        </div>
        <div className="md:col-span-3">
          <Label htmlFor="end_logradouro">Endereço</Label>
          <Input
            id="end_logradouro"
            value={valor.logradouro}
            onChange={(e) => patch({ logradouro: e.target.value })}
            onBlur={geocodificarAtual}
            placeholder="Rua, avenida…"
          />
        </div>
        <div className="md:col-span-1">
          <Label htmlFor="end_numero">Número</Label>
          <Input
            id="end_numero"
            value={valor.numero}
            onChange={(e) => patch({ numero: e.target.value })}
            onBlur={geocodificarAtual}
            placeholder="123"
            inputMode="numeric"
          />
        </div>
        <div className="md:col-span-3">
          <Label htmlFor="end_bairro">Bairro</Label>
          <Input
            id="end_bairro"
            value={valor.bairro}
            onChange={(e) => patch({ bairro: e.target.value })}
            placeholder="Centro"
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="end_cidade">Cidade</Label>
          <Input
            id="end_cidade"
            value={valor.cidade}
            onChange={(e) => patch({ cidade: e.target.value })}
            placeholder="Cidade"
          />
        </div>
        <div className="md:col-span-1">
          <Label htmlFor="end_uf">UF</Label>
          <Input
            id="end_uf"
            value={valor.uf}
            onChange={(e) => patch({ uf: e.target.value.toUpperCase().slice(0, 2) })}
            placeholder="UF"
            maxLength={2}
          />
        </div>
      </div>
    </div>
  );
}
